import * as math from 'mathjs';
import R6RTrafo from "./R6RTrafo";

class IkinPtpR6 {
  /**
   * Inverse kinematics for R6 robot using analytical method (PTP)
   * Based on R6_RTRAFO
   * 
   * @param {Matrix} F - Target frame (3x4 or 4x4)
   * @param {Array|Matrix} q - Initial joint angles (last solution or start pose)
   * @param {Object} r - Robot object with joint properties
   * @param {Array} mask - Mask vector (not used in this implementation)
   * @param {number} tIpo - Time step (not used in this implementation)
   * @returns {Array} [qs, status] - qs: solution joint angles, status: 'ok' or 'Probably out of workspace'
   */
  run(F, q, r, mask, tIpo) {
    let r6RTrafo = new R6RTrafo();
    let eps_q = 0.0001;

    let q_alt = [...math.squeeze(q).toArray ? math.squeeze(q).toArray() : math.squeeze(q)];
    let qs = [];
    let status = 'ok'; // Vorbesetzung

    let N = r.jointAmount; // Number of joints
    let hh = r.joints.map(j => j.h); // Joint types (1 = rotational, 0 = prismatic)

    let qmax = r.joints.map(j => j.qmax);
    let qmin = r.joints.map(j => j.qmin);

    let zahlposen = 1; // Only one pose for PTP (unlike LIN/CIRC)
    let anz = 0; // Number of valid solutions
    let qloes = []; // All valid solutions

    let F_akt = F; // Current target pose

    // Call R6_RTRAFO to get all 8 possible solutions
    let qz = r6RTrafo.run(F_akt, q_alt, r);

    // Check if qz is empty (warnung == 'ausserhalb')
    if (!qz || (Array.isArray(qz) && qz.length === 0)) {
      status = 'Probably out of workspace';
      return [[], status];
    }

    // qz is a matrix with 8 solution vectors (6x8)
    // Loop through all 8 solutions
    for (let iz = 0; iz < 8; iz++) {
      // Extract solution vector q = qz(:,iz+1) in MATLAB = qz[:,iz] in JS (0-based)
      let q_col = math.subset(qz, math.index(math.range(0, N), iz));
      q_col = math.squeeze(q_col);
      let q = [...(q_col.toArray ? q_col.toArray() : q_col)];

      // out_of_range script (matches MATLAB out_of_range.m)
      // Maps rotational joints to -2π to 2π range, then tries to shift into qmin/qmax range
      for (let ig = 0; ig < N; ig++) {
        if (hh[ig] === 1) { // rotat. Gelenk
          // q(ig,1) = q(ig,1) - fix(q(ig,1)/(2*pi+eps_q)) * 2*pi
          q[ig] = q[ig] - Math.floor(q[ig] / (2 * Math.PI + eps_q)) * 2 * Math.PI;
          
          // if qmin(ig,1) >= 0 & qmax(ig,1) > 0 & q(ig,1) < 0
          if (qmin[ig] >= 0 && qmax[ig] > 0 && q[ig] < 0) {
            q[ig] = q[ig] + 2 * Math.PI;
          }
          
          // if qmin(ig,1) < 0 & qmax(ig,1) >= 0
          if (qmin[ig] < 0 && qmax[ig] >= 0) {
            if (q[ig] > qmax[ig]) {
              q[ig] = q[ig] - 2 * Math.PI;
            }
            if (q[ig] < qmin[ig]) {
              q[ig] = q[ig] + 2 * Math.PI;
            }
          }
          
          // if qmin(ig,1) < 0 && qmax(ig,1) <= 0 & q(ig,1) > 0
          if (qmin[ig] < 0 && qmax[ig] <= 0 && q[ig] > 0) {
            q[ig] = q[ig] - 2 * Math.PI;
          }
        }
      }
      
      // Check if all joints are within qmin and qmax
      let out_range = 0; // Im Arbeitsbereich: out_range=0
      for (let k = 0; k < N; k++) {
        if (q[k] > (qmax[k] + eps_q) || q[k] < (qmin[k] - eps_q)) {
          out_range = 1; // aktueller Vektor q wird nicht in die Lösungsmenge aufgenommen
        }
      }

      if (out_range === 0) {
        // Solution is in working range
        anz = anz + 1;
        qloes.push(q);
      }
    }

    // NEU: April 2023 - Weighted distances for selecting best solution
    // c_g = [0.3 0.3 0.15 0.1 0.1 0.05] - weights for joints 1-6, sum is 1
    let c_g = [0.3, 0.3, 0.15, 0.1, 0.1, 0.05];
    // d_g = diag(c_g) - diagonal weighting matrix
    let d_g = math.diag(c_g);

    if (anz > 0) {
      // Calculate weighted distances to q_alt for all valid solutions
      let abw = [];
      for (let i1 = 0; i1 < anz; i1++) {
        // NEU: April 2023 - Weighted distance calculation
        // dq_gew = d_g * (q_alt - qloes(:,i1))
        let dq = q_alt.map((q_alt_val, idx) => q_alt_val - qloes[i1][idx]);
        let dq_gew = math.multiply(d_g, math.matrix(dq));
        // abw(i1) = norm(dq_gew)
        abw.push(math.norm(dq_gew));
      }

      // Find solution with minimum weighted distance
      // [wert_norm, min_loes] = min(abw)
      let min_abw = Math.min(...abw);
      let min_loes = abw.indexOf(min_abw);
      
      // qs = qloes(:,min_loes) - solution with minimum distance to q_alt
      qs = qloes[min_loes];
    }

    if (anz === 0) {
      // No valid solutions found
      status = 'Probably out of workspace';
      return [[], status];
    }

    return [qs, status];
  }
}

export default IkinPtpR6;
